import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  SiteService,
  Site,
  CreateSiteData,
  UpdateSiteData,
  AxiosErrorResponse,
  PaginationParams,
  PaginationMeta,
} from "@/lib/api/services/siteService";

export interface SiteState {
  sites: Site[];
  currentSite: Site | null;
  isLoading: boolean;
  error: string | null;
  selectedOrganizationId: number | null;
  pagination?: PaginationMeta;
  searchTerm?: string;
}

const defaultInitialState: SiteState = {
  sites: [],
  currentSite: null,
  isLoading: false,
  error: null,
  selectedOrganizationId: null,
  pagination: undefined,
  searchTerm: undefined,
};

const initialState: SiteState = defaultInitialState;

const handleApiError = (error: unknown): string => {
  const axiosError = error as AxiosErrorResponse;
  if (axiosError.response?.data) {
    const data = axiosError.response.data;
    if (Array.isArray(data.message)) {
      return data.message[0];
    }
    return data.message || "An unexpected error occurred";
  }
  return (axiosError.message as string) || "An unexpected error occurred";
};

export const getSites = createAsyncThunk(
  "site/getSites",
  async (
    {
      organizationId,
      params,
    }: { organizationId: number; params?: PaginationParams },
    { rejectWithValue }
  ) => {
    try {
      return await SiteService.getSites(organizationId, params);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getSiteById = createAsyncThunk(
  "site/getSiteById",
  async (
    { organizationId, siteId }: { organizationId: number; siteId: number },
    { rejectWithValue }
  ) => {
    try {
      return await SiteService.getSiteById(organizationId, siteId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const createSite = createAsyncThunk(
  "site/createSite",
  async (
    {
      organizationId,
      siteData,
    }: { organizationId: number; siteData: CreateSiteData },
    { rejectWithValue }
  ) => {
    try {
      return await SiteService.createSite(organizationId, siteData);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const updateSite = createAsyncThunk(
  "site/updateSite",
  async (
    {
      organizationId,
      siteId,
      siteData,
    }: {
      organizationId: number;
      siteId: number;
      siteData: UpdateSiteData;
    },
    { rejectWithValue }
  ) => {
    try {
      return await SiteService.updateSite(organizationId, siteId, siteData);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const deleteSite = createAsyncThunk(
  "site/deleteSite",
  async (
    { organizationId, siteId }: { organizationId: number; siteId: number },
    { rejectWithValue }
  ) => {
    try {
      return await SiteService.deleteSite(organizationId, siteId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

const siteSlice = createSlice({
  name: "site",
  initialState,
  reducers: {
    setSelectedOrganization: (state, action: PayloadAction<number>) => {
      state.selectedOrganizationId = action.payload;
    },
    setCurrentSite: (state, action: PayloadAction<Site | null>) => {
      state.currentSite = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetSites: (state) => {
      state.sites = [];
      state.currentSite = null;
      state.isLoading = false;
      state.error = null;
      state.selectedOrganizationId = null;
      state.pagination = undefined;
      state.searchTerm = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSites.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSites.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sites = action.payload.sites;
        state.pagination = action.payload.pagination;
      })
      .addCase(getSites.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getSiteById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSiteById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSite = action.payload;
      })
      .addCase(getSiteById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createSite.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSite.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sites.push(action.payload.site);
        state.currentSite = action.payload.site;
      })
      .addCase(createSite.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateSite.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSite.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.sites.findIndex(
          (s) => s.id === action.payload.site.id
        );
        if (index !== -1) {
          state.sites[index] = action.payload.site;
        }
        if (state.currentSite?.id === action.payload.site.id) {
          state.currentSite = action.payload.site;
        }
      })
      .addCase(updateSite.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteSite.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSite.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedSiteId = action.meta.arg.siteId;
        state.sites = state.sites.filter((s) => s.id !== deletedSiteId);
        if (state.currentSite?.id === deletedSiteId) {
          state.currentSite = null;
        }
      })
      .addCase(deleteSite.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedOrganization,
  setCurrentSite,
  setSearchTerm,
  clearError,
  resetSites,
} = siteSlice.actions;
export default siteSlice.reducer;
